import type { ReactNode } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { SelectField } from '@clay-run/glaze/select-field';
import { fmtDateOnly } from '../cells';
import { DetailKV, type FlowDetailPanel } from '../FlowDetail';
import { SignalRowActions } from './SignalRowActions';
import { dueTone, DUE_TONE_CLASS } from './dueTone';
import { SignalActionArchiveForm } from './SignalActionArchiveForm';
import type { CardSignal, SignalActionCardModel } from './useSignalActionCard';
import {
  CHIP_COLOR,
  flowLabel,
  gongStatusLine,
  optionLabel,
  stepLabel,
  type GongStatusLineInput,
} from './signalLeadPresentation';

// Re-exported so `__tests__/SignalActionCard.test.tsx` (which predates the
// lift) keeps importing `gongStatusLine` from this file unchanged — the
// definition itself lives in `signalLeadPresentation.ts` now (coding
// standards §1, "V1 imports it back").
export { gongStatusLine };

/**
 * The Signal card (QA batch 2 unit B8, D-I; qa-batch4-plan.md D-A;
 * qa-batch5-plan.md D-B/D-C) — the FIRST cell of the panel grid, left of
 * Contact Info, in the Signals tab's expanded contact card (`SignalDetail`
 * -> `ContactDetail.leadPanel` -> `FlowDetail`; `LeadPanelCard` in
 * `SignalDetail`'s pre-loaded branches). It is about THIS TASK.
 *
 * Pure rendering over ONE `SignalActionCardModel` (`useSignalActionCard`,
 * which `SignalDetail` owns — see that hook's doc for the selector,
 * auto-advance/collapse and archive-intent rules). `signalLeadPanel(model)`
 * builds the `FlowDetailPanel` in two pieces:
 *
 * - **Header** (`headerAction`, `SignalCardHeaderActions`) — right of the
 *   SIGNAL label on the same line: `[Verb] [Mark as read] [Archive]`
 *   (`SignalRowActions layout="card"`), right-aligned. Two independent wrap
 *   points (fix-plan-batch5.md U2/D2): the cluster itself grows from basis
 *   `auto` (`grow`, never `flex-1`'s basis-0), so the shared
 *   `PANEL_LABEL_ROW_CLASS` (`flex-wrap`) can drop it under the SIGNAL label
 *   when it does not fit beside it; at smaller widths still, its own button
 *   row wraps onto a second line, still right-aligned. Resets the label
 *   row's uppercase/tracking/weight. Nothing renders when no signal is open
 *   (the header reads SIGNAL alone).
 * - **Body** (`content`, `SignalCardBody`) — first line: the one Gong status
 *   sentence (qa-batch4 D-B, `gongStatusLine`), directly under the SIGNAL
 *   row (Spencer's batch-5 override of the plan's header placement); then
 *   the Glaze `SelectField`
 *   (always a real dropdown, even with one option; `aria-label` only — the
 *   header already says Signal, QA batch 5 item 3), the details as a
 *   flowing 2-column `DetailKV` grid in the order Flow, Step, Due, Days
 *   open, Assignee, Status (absent fields omitted, never a guessed
 *   fallback; odd count → the last spans both columns), and the inline
 *   archive form when the rep opened it. Collapsed: "No open signals".
 * - **Never wider than its cell** — the body grid's single track is
 *   `minmax(0,1fr)` (an implicit `auto` track grows to the select's
 *   min-content, i.e. the full nowrap option label, which pushed the select
 *   and the DetailKV hairlines past the card into Contact Info); the select
 *   is `min-w-0 max-w-full`, Glaze truncates its value with an ellipsis,
 *   and the full label rides a `title`.
 */

export type { GongStatusLineInput };

/** Header piece (qa-batch5 D-B) — the action buttons only; `null` when nothing is open. */
export function SignalCardHeaderActions({ model }: { model: SignalActionCardModel }) {
  const { selected, actions, now } = model;
  if (!selected || !actions) return null;
  return (
    <div
      data-testid="signal-card-header-actions"
      className="ml-auto flex min-w-0 grow flex-wrap items-center justify-end gap-x-2 gap-y-1 text-right normal-case tracking-normal font-normal"
    >
      {/* `key={selected.signal_id}` (fix-plan-batch2.md F6/D-F): forces a
          real unmount+remount on every selector switch, so A's Snooze
          popover / inline error never stays mounted under B's `row`. Each
          mutation's `onActed` rides its hook's own `onSuccess` (D-J) and
          lands on the card's `handleActed`, never the table's. */}
      <SignalRowActions
        key={selected.signal_id}
        row={selected}
        now={now}
        layout="card"
        onActed={model.handleActed}
        archiveExpanded={model.archiveFormOpen}
        onArchiveRequest={() => model.setArchiveFormOpen(!model.archiveFormOpen)}
      />
    </div>
  );
}

interface DetailField {
  k: string;
  v: ReactNode;
}

/** qa-batch5 D-C — the six details in order, absent ones omitted, as a flowing 2-column grid. */
function SignalCardDetails({ selected, actions, now }: { selected: CardSignal; actions: NonNullable<SignalActionCardModel['actions']>; now: Date }) {
  const tone = dueTone(selected.due_date, now);
  const flow = flowLabel(selected);
  const stepText = selected.step_number != null ? `Step ${selected.step_number} · ${stepLabel(selected)}` : stepLabel(selected);
  const fields: DetailField[] = [];
  if (flow) fields.push({ k: 'Flow', v: <span title={flow}>{flow}</span> });
  fields.push({ k: 'Step', v: <span title={stepText}>{stepText}</span> });
  if (selected.due_date) {
    fields.push({
      k: 'Due',
      v: (
        <span data-testid="signal-card-due" className={tone ? DUE_TONE_CLASS[tone] : ''}>
          {fmtDateOnly(selected.due_date)}
        </span>
      ),
    });
  }
  if (selected.days_open != null) {
    fields.push({ k: 'Days open', v: <span data-testid="signal-card-days-open">{selected.days_open}</span> });
  }
  if (selected.assignee) {
    fields.push({ k: 'Assignee', v: <span data-testid="signal-card-assignee" title={selected.assignee}>{selected.assignee}</span> });
  }
  if (actions.chip) {
    fields.push({
      k: 'Status',
      v: (
        <span data-testid="signal-card-chip">
          <Badge color={CHIP_COLOR[actions.chip.kind]} size="xs">{actions.chip.label}</Badge>
        </span>
      ),
    });
  }
  const odd = fields.length % 2 === 1;
  // Two-column hairlines: every `DetailKV` keeps `border-b` + `last:border-b-0`;
  // with an even count the whole last ROW (two cells) drops its border so the
  // line is continuous across both cells and absent under the final row. With
  // an odd count the spanning last cell is the only last-row cell and `last:`
  // already handles it.
  return (
    <div data-testid="signal-card-meta" className="@container min-w-0">
      <div
        data-testid="signal-card-meta-grid"
        className={`grid min-w-0 grid-cols-1 gap-x-4 @min-[22rem]:grid-cols-2 ${odd ? '' : '@min-[22rem]:[&>:nth-last-child(-n+2)]:border-b-0'}`}
      >
        {fields.map((f, i) => (
          <DetailKV
            key={f.k}
            k={f.k}
            v={f.v}
            labelWidth="narrow"
            className={odd && i === fields.length - 1 ? '@min-[22rem]:col-span-2' : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/** Body piece (qa-batch5 D-B/D-C) — selector, details, inline archive form; or the collapsed line. */
export function SignalCardBody({ model }: { model: SignalActionCardModel }) {
  const { selected, actions, options, now } = model;
  if (!selected || !actions) {
    // No open (To do / Snoozed) Signal Actions — from the start, or because
    // the rep just cleared the last one from this card. The header already
    // says SIGNAL, so the body is the bare sentence.
    return (
      <div data-testid="signal-card-empty" className="py-0.5 text-[13px] text-content-tertiary dark:text-nightshade-500">
        No open signals
      </div>
    );
  }
  const selectedLabel = optionLabel(selected);
  const gongStatus = selected.signal_instance_id
    ? gongStatusLine({ state: actions.state, verifiedAt: selected.gong_verified_at, now, ...model.gong })
    : null;
  return (
    <div data-testid="signal-card-body" className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2.5">
      {gongStatus && (
        <div data-testid="signal-card-gong-status" className="text-[12px] text-content-tertiary dark:text-nightshade-500">
          {gongStatus}
        </div>
      )}
      <div data-testid="signal-card-select" className="min-w-0 max-w-full" title={selectedLabel}>
        <SelectField<string>
          aria-label="Signal"
          className="min-w-0 max-w-full"
          items={options.map(o => ({ value: o.signal_id, label: optionLabel(o) }))}
          value={selected.signal_id}
          onValueChange={(value: string | null) => { if (value) model.selectId(value); }}
          size="sm"
        />
      </div>

      <SignalCardDetails selected={selected} actions={actions} now={now} />

      {model.archiveFormOpen && (
        <div ref={model.formRef} className="border-t border-secondary pt-2.5 dark:border-nightshade-700">
          <SignalActionArchiveForm
            key={selected.signal_id}
            signalId={selected.signal_id}
            signalType={selected.signal_type}
            archivePrefill={actions.archivePrefill}
            initialDraft={model.archiveDrafts.get(selected.signal_id)}
            onDraftChange={draft => model.retainArchiveDraft(selected.signal_id, draft)}
            onActed={model.handleActed}
            onDone={() => model.setArchiveFormOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

/** The Signal card as a `FlowDetailPanel` — header actions + body from ONE model (qa-batch5 D-B). */
export function signalLeadPanel(model: SignalActionCardModel): FlowDetailPanel {
  return {
    key: 'signal',
    label: 'Signal',
    headerAction: <SignalCardHeaderActions model={model} />,
    content: <SignalCardBody model={model} />,
  };
}
